import SearchField from '@pelckmans/elementis/components/search-field';
import debounce from 'lodash/debounce';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ModuleContext from '../../_contexts/ModuleContext';
import { search, SearchResult } from './../api/search-api';
import { useSearchParams } from 'react-router-dom';

interface Props {
  setSearchResults: (results: SearchResult) => void;
  setIsLoading: (isLoading: boolean) => void;
}

function AdvancedSearchField({ setSearchResults, setIsLoading }: Props) {
  const { module } = useContext(ModuleContext);
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const searchQuery = searchParams.get('q') || '';
  const [searchText, setSearchText] = React.useState<string>(searchQuery);
  const trimmedSearchText = searchText.trim();

  const handleClear = () => {
    setSearchResults([]);
    setSearchText('');
    setSearchParams({});
  };

  const handleSearch = useCallback(
    async (value: string) => {
      if (!module) return;
      const trimmedValue = value.trim();
      if (!trimmedValue) {
        setSearchResults([]);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      const items = await search(module.id, trimmedValue);
      setSearchResults(items);
      setIsLoading(false);
    },
    [module, setIsLoading, setSearchResults],
  );

  const debouncedUpdateSearchParams = useMemo(
    () =>
      debounce((value: string) => {
        setSearchParams({ q: value.trim() });
      }, 500),
    [setSearchParams],
  );

  useEffect(() => {
    if (searchQuery) {
      handleSearch(searchQuery);
    } else {
      setSearchResults([]);
      setIsLoading(false);
    }
  }, [searchQuery, handleSearch, setSearchResults, setIsLoading]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchText(value);

    if (value.trim() === '') {
      setSearchResults([]);
      debouncedUpdateSearchParams.cancel();
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('q');
        return newParams;
      });
      return;
    }

    debouncedUpdateSearchParams(value);
  };

  return (
    <SearchField
      value={searchText}
      placeholder={t('PAGES.SEARCH.HEADER.SEARCH_FIELD.PLACEHOLDER')}
      searchActionLabel={t('PAGES.SEARCH.HEADER.SEARCH_FIELD.SEARCH_ACTION_LABEL')}
      clearActionLabel={t('PAGES.SEARCH.HEADER.SEARCH_FIELD.CLEAR_ACTION_LABEL')}
      onChange={handleChange}
      onClear={handleClear}
      onSearch={() => {
        setSearchParams({ q: trimmedSearchText });
        handleSearch(trimmedSearchText);
      }}
    />
  );
}

export default AdvancedSearchField;
